import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 6-digit OTP verification card.
 * Props:
 *  - email: string
 *  - purpose: 'signup' | 'login'
 *  - onVerified: (payload) => void  // payload = { token, user }
 *  - onCancel: () => void
 */
export default function OtpVerification({ email, purpose, onVerified, onCancel }) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (fullCode) => {
    setError('');
    setIsVerifying(true);
    try {
      const r = await axios.post(`${API}/auth/verify-otp`, {
        email, code: fullCode, purpose,
      });
      onVerified(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Verification failed. Please try again.');
      setDigits(['', '', '', '', '', '']);
      // Restore focus AFTER inputs are re-enabled (isVerifying=false in finally).
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleChange = (i, v) => {
    const val = v.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = val;
    setDigits(next);
    if (val && i < 5) inputRefs.current[i + 1]?.focus();
    if (next.every(d => d)) submit(next.join(''));
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      e.preventDefault();
      const arr = paste.split('');
      setDigits(arr);
      submit(paste);
    }
  };

  const resend = async () => {
    setError('');
    setIsResending(true);
    try {
      await axios.post(`${API}/auth/resend-otp`, { email, purpose });
      setCooldown(30);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not resend code. Try again shortly.');
      if (e.response?.status === 429) {
        const m = /wait (\d+)s/.exec(e.response.data.detail || '');
        if (m) setCooldown(parseInt(m[1]));
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="max-w-md mx-auto text-center" data-testid="otp-verification-card">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-meritum-gold/15 mb-4">
        <Mail size={26} className="text-meritum-gold" />
      </div>
      <h2 className="font-heading text-2xl font-bold text-meritum-navy">Check your email</h2>
      <p className="font-body text-sm text-slate-500 mt-2">
        We sent a 6-digit code to <strong className="text-meritum-navy">{email}</strong>. It expires in 5 minutes.
      </p>

      <div className="flex justify-center gap-2 sm:gap-3 mt-6" onPaste={handlePaste}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={d}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            disabled={isVerifying}
            className="w-11 h-14 sm:w-12 sm:h-14 text-center text-2xl font-bold font-mono border-2 border-slate-200 rounded-lg bg-white text-meritum-navy focus:border-meritum-gold focus:outline-none focus:ring-2 focus:ring-meritum-gold/20 disabled:opacity-60"
            data-testid={`otp-digit-${i}`}
          />
        ))}
      </div>

      {isVerifying && (
        <p className="font-body text-xs text-slate-500 mt-3 inline-flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" />Verifying...
        </p>
      )}

      {error && <p className="font-body text-sm text-red-600 mt-4" data-testid="otp-error">{error}</p>}

      <div className="mt-6 text-sm text-slate-500">
        Didn't get it?{' '}
        <button
          onClick={resend}
          disabled={isResending || cooldown > 0}
          className="text-meritum-gold font-semibold hover:text-meritum-teal disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="otp-resend-btn"
        >
          {isResending ? 'Sending...' : (cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code')}
        </button>
      </div>

      {onCancel && (
        <button
          onClick={onCancel}
          className="mt-4 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
          data-testid="otp-back-btn"
        >
          <ArrowLeft size={12} />Use a different email
        </button>
      )}
    </div>
  );
}
